import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { mangaList } from '@/mocks/manga';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!manga) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-50">
        <span className="w-16 h-16 flex items-center justify-center text-3xl text-foreground-300 mb-4">
          <i className="ri-emotion-sad-line"></i>
        </span>
        <h1 className="text-xl font-bold text-foreground-950 font-heading mb-2">漫画未找到</h1>
        <p className="text-sm text-foreground-500 mb-6">抱歉，您要找的漫画不存在</p>
        <Link
          to="/"
          className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedManga = mangaList
    .filter((m) => m.category === manga.category && m.id !== manga.id)
    .slice(0, 4);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: manga.title,
            description: manga.description,
            author: { '@type': 'Person', name: manga.author },
            datePublished: manga.chapters[0]?.updateDate || manga.updateDate,
            dateModified: manga.updateDate,
            image: manga.coverImage,
            url: `https://www.muguamanhua.com/manga/${manga.id}`,
            publisher: { '@type': 'Organization', name: '木瓜漫画' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.muguamanhua.com/' },
              { '@type': 'ListItem', position: 2, name: `${manga.category}漫画`, item: `https://www.muguamanhua.com/category/${manga.category === '热血' ? 'rexue' : manga.category === '恋爱' ? 'lianai' : manga.category === '悬疑' ? 'xuanyi' : manga.category === '搞笑' ? 'gaoxiao' : manga.category === '奇幻' ? 'qihuan' : manga.category === '科幻' ? 'kehuan' : manga.category === '冒险' ? 'maoxian' : 'xiaoyuan'}` },
              { '@type': 'ListItem', position: 3, name: manga.title, item: `https://www.muguamanhua.com/manga/${manga.id}` },
            ],
          }),
        }}
      />

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: `${manga.category}漫画`, path: `/category/${manga.category === '热血' ? 'rexue' : manga.category === '恋爱' ? 'lianai' : manga.category === '悬疑' ? 'xuanyi' : manga.category === '搞笑' ? 'gaoxiao' : manga.category === '奇幻' ? 'qihuan' : manga.category === '科幻' ? 'kehuan' : manga.category === '冒险' ? 'maoxian' : 'xiaoyuan'}` },
          { label: manga.title },
        ]}
      />

      {/* Manga Detail */}
      <section className="py-8 md:py-12 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 w-full sm:w-56 md:w-64 mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                <img
                  src={manga.coverImage}
                  alt={manga.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-2">
                {manga.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-3">
                {manga.author} 作品 · {manga.category} · {manga.subCategory}
              </p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-sm font-medium text-secondary-600">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill"></i>
                  </span>
                  {manga.rating}
                </span>
                <span className="text-sm text-foreground-500">
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className="ri-eye-line"></i>
                  </span>
                  {formatViews(manga.views)} 阅读
                </span>
                <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                  manga.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-foreground-200 text-foreground-700'
                }`}>
                  {manga.status}
                </span>
                <span className="text-sm text-foreground-500">更新于 {manga.updateDate}</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                {manga.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  开始阅读第一话
                </button>
                <button
                  type="button"
                  className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 border border-background-200/70 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className="ri-bookmark-line"></i>
                  </span>
                  加入书架
                </button>
                <button
                  type="button"
                  className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 border border-background-200/70 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className="ri-share-forward-line"></i>
                  </span>
                  分享
                </button>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <div className="mt-10">
            <h2 className="text-lg font-bold text-foreground-950 font-heading mb-4">章节列表</h2>
            <div className="border border-background-200/70 rounded-lg overflow-hidden divide-y divide-background-200/70">
              {manga.chapters.map((ch, index) => (
                <div
                  key={ch.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-400 font-mono w-8">{index + 1}</span>
                    <span className="text-sm text-foreground-800 font-medium">{ch.title}</span>
                  </div>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.updateDate}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Content / Description */}
          <div className="mt-10">
            <h2 className="text-lg font-bold text-foreground-950 font-heading mb-4">作品简介</h2>
            <div className="text-sm text-foreground-700 leading-relaxed space-y-3 bg-background-100 rounded-lg p-5 border border-background-200/70">
              <div dangerouslySetInnerHTML={{ __html: manga.contentHtml }} />
            </div>
          </div>

          {/* Related Manga */}
          {relatedManga.length > 0 && (
            <div className="mt-10">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-4">
                更多{manga.category}漫画
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedManga.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/manga/${rm.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={rm.coverImage}
                        alt={rm.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-2">
                      <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 truncate font-heading">
                        {rm.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}